import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { extractApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePreferences } from "@/lib/preferences";
import { Wordmark } from "@/components/Wordmark";
import { ReadAloud } from "@/components/TTS";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowRight, ArrowLeft, ShieldCheck, AlertTriangle, Lightbulb } from "lucide-react";

const STEPS = [
  {
    id: "welcome",
    title: "Welcome — here's what CRA3D is",
    icon: Lightbulb,
    body: [
      "CRA3D helps you describe your real experience, recognize the strengths you've actually demonstrated, discover interests, and consider real-life constraints.",
      "At the end of the assessment, you'll receive 3 understandable, AI-supported Lanes of Direction to choose from.",
      "You always have the final word. You can correct or reject every conclusion.",
    ],
    testid: "onboarding-step-welcome",
  },
  {
    id: "not",
    title: "What CRA3D is NOT",
    icon: AlertTriangle,
    body: [
      "CRA3D does not guarantee jobs, interviews, or income.",
      "CRA3D does not scientifically discover one perfect life purpose.",
      "CRA3D does not predict that your job will disappear.",
      "CRA3D does not make medical, psychological, legal, or financial determinations.",
      "CRA3D does not provide accredited certification.",
      "CRA3D does not replace your effort and your own judgment.",
    ],
    testid: "onboarding-step-not",
  },
  {
    id: "ai_reality",
    title: "AI is a helper, not a decision-maker",
    icon: ShieldCheck,
    body: [
      "AI can make mistakes. Every answer you use should be verified.",
      "Please do not enter private or confidential information: SSNs, banking, medical records, or confidential employer or customer material. CRA3D never asks for these.",
      "Human effort is required. This works when you do.",
    ],
    testid: "onboarding-step-ai",
  },
];


export default function Onboarding() {
  const [params] = useSearchParams();
  const isRevisit = params.get("revisit") === "1";
  const { user } = useAuth();
  const { prefs, update } = usePreferences();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const totalSteps = STEPS.length + 1; // + preferences step

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const onFinish = async () => {
    setSaving(true);
    setError(null);
    try {
      await update({ onboarding_completed: true });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(extractApiError(err, "Could not save your preferences."));
    } finally {
      setSaving(false);
    }
  };

  const togglePref = async (key, value) => {
    try { await update({ [key]: value }); } catch (_) { /* toast? */ }
  };

  return (
    <div className="min-h-screen bg-bg-base text-fg-primary flex flex-col">
      <header className="border-b border-border-subtle bg-bg-surface">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard"><Wordmark size="sm" testid="onboarding-header-wordmark" /></Link>
          <span
            data-testid="onboarding-progress-label"
            className="text-xs text-fg-secondary"
          >
            Step {step + 1} of {totalSteps}
          </span>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          {/* progress bar */}
          <div className="w-full h-1.5 bg-bg-surface rounded-full overflow-hidden mb-8">
            <div
              data-testid="onboarding-progress-bar"
              className="h-full bg-accent-warm transition-[width] duration-300"
              style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
            />
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6" data-testid="onboarding-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step < STEPS.length && (
            <IntroStep step={STEPS[step]} />
          )}

          {step === STEPS.length && (
            <PreferencesStep prefs={prefs} onToggle={togglePref} />
          )}

          <div className="mt-10 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="onboarding-back-btn"
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover disabled:opacity-40"
            >
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
              Back
            </Button>
            {step < STEPS.length ? (
              <Button
                onClick={() => setStep((s) => s + 1)}
                data-testid="onboarding-next-btn"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                Continue
                <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
              </Button>
            ) : (
              <Button
                onClick={onFinish}
                disabled={saving}
                data-testid="onboarding-finish-btn"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                {isRevisit ? "Save and return" : (saving ? "Saving…" : "Take me to my dashboard")}
                <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function IntroStep({ step }) {
  const Icon = step.icon;
  const speakText = `${step.title}. ${step.body.join(" ")}`;
  return (
    <section data-testid={step.testid}>
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-accent-warm/10 text-accent-warm">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <h1 className="text-2xl sm:text-3xl font-heading font-medium text-fg-primary">{step.title}</h1>
      </div>
      <div className="mb-4">
        <ReadAloud text={speakText} testidPrefix={`onboarding-${step.id}-tts`} />
      </div>
      <ul className="space-y-3">
        {step.body.map((p, i) => (
          <li key={i} className="text-base leading-relaxed text-fg-secondary">
            {p}
          </li>
        ))}
      </ul>
    </section>
  );
}

function PreferencesStep({ prefs, onToggle }) {
  const immediate = [
    { key: "plain_language", title: "Plain-language text", detail: "When we have a simpler version of copy, prefer it." },
    { key: "read_aloud", title: "Read-aloud", detail: "Show device / browser-generated speech controls on questions and steps." },
    { key: "larger_text", title: "Larger text", detail: "Bump the whole app's font size a step up." },
    { key: "high_contrast", title: "High contrast", detail: "Use a higher-contrast theme, applied immediately." },
    { key: "reduced_motion", title: "Reduced motion", detail: "Turn off non-essential transitions. Respects your OS setting regardless." },
  ];
  const future = [
    { key: "examples_first", title: "Show examples first", detail: "Applied in upcoming course features." },
    { key: "step_by_step", title: "Step-by-step guidance", detail: "Applied in upcoming course features." },
    { key: "practice_first", title: "Practice-first learning", detail: "Applied in upcoming course features." },
    { key: "short_lessons", title: "Short lesson preference", detail: "Applied in upcoming course features." },
  ];

  const Row = ({ item, group }) => (
    <div
      className="flex items-start justify-between gap-4 py-3 border-b border-border-subtle last:border-b-0"
      data-testid={`pref-row-${item.key}`}
    >
      <div>
        <Label htmlFor={`pref-${item.key}`} className="text-base text-fg-primary">{item.title}</Label>
        <p className="text-xs text-fg-secondary mt-1">{item.detail}</p>
      </div>
      <Switch
        id={`pref-${item.key}`}
        checked={!!prefs?.[item.key]}
        onCheckedChange={(v) => onToggle(item.key, v)}
        data-testid={`pref-switch-${item.key}`}
      />
    </div>
  );

  return (
    <section data-testid="onboarding-step-preferences">
      <h1 className="text-2xl sm:text-3xl font-heading font-medium text-fg-primary">Your preferences</h1>
      <p className="mt-2 text-sm text-fg-secondary">You can change these any time from Settings.</p>

      <div className="mt-6 rounded-lg border border-border-subtle bg-bg-surface p-6">
        <p className="text-xs uppercase tracking-wider text-fg-secondary mb-2">Applied immediately</p>
        {immediate.map((i) => <Row key={i.key} item={i} group="immediate" />)}
      </div>
      <div className="mt-4 rounded-lg border border-border-subtle bg-bg-surface p-6">
        <p className="text-xs uppercase tracking-wider text-fg-secondary mb-2">Saved for upcoming course features</p>
        {future.map((i) => <Row key={i.key} item={i} group="future" />)}
      </div>
    </section>
  );
}
